import CryptoJS from 'crypto-js'

import { configs } from '@configs'
import { DataStore, EMkStorageKeys, EMkStorageOpts } from './DataStore'
import { sanitizeAuthenticatedSession, sanitizeAuthorizedSession } from './sessionSanitizer'
import { isAuthenticatedSession, isAuthorizedSession } from './sessionTypeGuards'

/*
  Recommendations:

  Sensitive Tokens: Session IDs and Access Tokens should ideally be stored in HttpOnly, Secure cookies set by the backend server, not by frontend code.

  Non-Sensitive Data: ProtectedDataStore is fine for storing user preferences or non-critical UI state where obfuscation is sufficient to prevent accidental modification or casual viewing.

  Do not store PII: Do not store Personally Identifiable Information (PII) or secrets (passwords, private keys) using this store.
 */

export interface CookieOptions {
  expires?: Date | number
  path?: string
  domain?: string
  secure?: boolean
  sameSite?: 'Strict' | 'Lax' | 'None'
  shouldEncrypt?: boolean
}

export class ProtectedDataStore extends DataStore {
  private static readonly ENCRYPTION_KEY = CryptoJS.enc.Utf8.parse(configs.mk.storageSecret).toString(CryptoJS.enc.Utf8)

  private static readonly ENCRYPTION_OPTIONS = {
    iv: CryptoJS.lib.WordArray.random(128 / 8),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }

  public static getItem<T = any>(key: EMkStorageKeys | string, opts?: EMkStorageOpts): T | null {
    const { defaultValue = null, parse = true, store = sessionStorage } = { ...opts }

    const item = store?.getItem(key)

    if (!item) return defaultValue || null

    const decryptedData = parse ? JSON.parse(ProtectedDataStore.decryptData(item)) : ProtectedDataStore.decryptData(item)

    if (decryptedData && typeof decryptedData === 'object' && 'data' in decryptedData && 'expirationTime' in decryptedData) {
      const now = Date.now()

      if (now > decryptedData.expirationTime) {
        ProtectedDataStore.removeItem(key, { store })
        return defaultValue || null
      }

      return ProtectedDataStore.sanitizeData<T>(decryptedData.data, key)
    }

    return ProtectedDataStore.sanitizeData<T>(decryptedData, key)
  }

  public static setItem<T = any>(key: EMkStorageKeys | string, value: T, opts?: EMkStorageOpts): void {
    const { store = sessionStorage, expirationTime } = { ...opts }

    // Create a wrapper object that includes expiration time if provided
    const dataToEncrypt = expirationTime ? { data: value, expirationTime, encryptedAt: Date.now() } : value

    store?.setItem(key, ProtectedDataStore.encryptData(JSON.stringify(dataToEncrypt)))
  }

  public static removeItem(key: EMkStorageKeys | string, opts?: EMkStorageOpts): void {
    const { store = sessionStorage } = { ...opts }
    store?.removeItem(key)
  }

  public static clearAll(opts?: EMkStorageOpts): void {
    const { store = sessionStorage } = { ...opts }
    store?.clear()
  }

  public static setCookie(name: string, value: any, options: CookieOptions = {}): void {
    // Handle string values directly without JSON.stringify to avoid double encoding
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
    const encryptedValue = options.shouldEncrypt ? this.encryptData(stringValue) : stringValue
    let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(encryptedValue)}`

    if (options.expires) {
      let expiresDate: Date
      if (typeof options.expires === 'number') {
        expiresDate = new Date()
        expiresDate.setTime(expiresDate.getTime() + options.expires * 24 * 60 * 60 * 1000)
      } else if (options.expires instanceof Date) {
        expiresDate = options.expires
      } else {
        expiresDate = new Date()
      }
      cookieString += `; expires=${expiresDate.toUTCString()}`
    }

    if (options.path) cookieString += `; path=${options.path}`
    if (options.domain) cookieString += `; domain=${options.domain};`
    if (options.secure) cookieString += '; secure'
    if (options.sameSite) cookieString += `; samesite=${options.sameSite}`

    document.cookie = cookieString
  }

  public static getCookie<T = any>(name: string): T | null {
    const cookies = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .filter((cookie) => cookie.includes('='))
    const found = cookies.find((cookie) => {
      const idx = cookie.indexOf('=')
      if (idx === -1) return false
      const cookieName = cookie.slice(0, idx).trim()
      return cookieName === name
    })
    if (!found) return null
    const idx = found.indexOf('=')
    const cookieValue = found.slice(idx + 1).trim()
    try {
      const decryptedValue = this.decryptData(decodeURIComponent(cookieValue))
      // Try to parse as JSON first, if that fails, return as string
      try {
        return JSON.parse(decryptedValue)
      } catch {
        // If JSON.parse fails, it's likely a string value, return as-is
        return decryptedValue as T
      }
    } catch (error) {
      console.error(`Error decrypting cookie ${name}:`, error)
      return null
    }
  }

  public static removeCookie(name: string, options: CookieOptions = {}): void {
    const opts = { ...options, expires: new Date(0), shouldEncrypt: false }
    this.setCookie(name, '', opts)
  }

  private static encryptData = (data: string): string => {
    const encryptedData = CryptoJS.AES.encrypt(data, ProtectedDataStore.ENCRYPTION_KEY, ProtectedDataStore.ENCRYPTION_OPTIONS).toString()
    return CryptoJS.enc.Utf8.stringify(CryptoJS.enc.Utf8.parse(encryptedData))
  }

  private static decryptData = (ciphertext: string): string => {
    const data = CryptoJS.enc.Utf8.parse(ciphertext).toString(CryptoJS.enc.Utf8)
    const bytes = CryptoJS.AES.decrypt(data, ProtectedDataStore.ENCRYPTION_KEY).toString(CryptoJS.enc.Utf8)
    return bytes
  }

  private static sanitizeData<T>(data: any, key: EMkStorageKeys | string): T {
    // For known session types, use specific sanitizers
    if (key === EMkStorageKeys.MAKO_SESSION) {
      // Use type guards for reliable session type detection
      if (isAuthorizedSession(data)) {
        return sanitizeAuthorizedSession(data) as T
      }
      if (isAuthenticatedSession(data)) {
        return sanitizeAuthenticatedSession(data) as T
      }

      // Fallback for legacy data without sessionType - try to infer from properties
      if (data && typeof data === 'object') {
        if ('authorizedUser' in data) {
          return sanitizeAuthorizedSession(data) as T
        }
        if ('authenticatedUser' in data) {
          return sanitizeAuthenticatedSession(data) as T
        }
      }
    }

    // For other types, return as-is (no sanitization needed for non-session data)
    // This maintains backward compatibility for other storage keys
    return data as T
  }
}
